// src/utils/testNavigation.js
// Utility hook for test type navigation across mock test, mini mock test, and class test

import { useLocation } from "react-router-dom";
import { 
  TEST_TYPES, 
  TEST_ROUTES, 
  TEST_BACK_ROUTES,
  TEST_RESULT_ROUTES,
  TEST_ATTEMPT_ROUTES 
} from "../features/mocktests/mockTestSlice";

/**
 * Determines the test type from the current URL path
 * @param {string} pathname - The current URL path
 * @returns {string} The test type (full_mock, mini_mock, or class_test)
 */
export function getTestTypeFromPath(pathname) {
  if (pathname.includes("/bundle-test")) {
    return TEST_TYPES.BUNDLE_TEST;
  } else if (pathname.includes("/mini-mock-test") || pathname.includes("/minimocktest")) {
    return TEST_TYPES.MINI_MOCK;
  } else if (pathname.includes("/class-test") || pathname.includes("/classtest")) {
    return TEST_TYPES.CLASS_TEST;
  }
  return TEST_TYPES.MOCK_TEST;
}

/**
 * Custom hook that provides test type and navigation utilities
 * based on the current URL path
 * 
 * @returns {Object} Object containing:
 *   - testType: The current test type string for API calls
 *   - baseRoute: The base route path for navigation (e.g., "/mock-test")
 *   - backRoute: The route to go back to the test list
 *   - resultRoute: The route prefix for results
 *   - attemptRoute: The route prefix for attempts
 *   - getTestUrl: Function to build test URLs with the correct base
 *   - getResultsUrl: Function to build results URLs
 *   - getAttemptResultUrl: Function to build attempt result URLs
 *   - getAttemptsUrl: Function to build attempts log URLs
 */
export function useTestNavigation() {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Determine test type from URL
  const testType = getTestTypeFromPath(pathname);
  
  // Get the base route for this test type
  const baseRoute = TEST_ROUTES[testType];
  
  // Get the back route for returning to list
  const backRoute = TEST_BACK_ROUTES[testType];
  
  // Get result and attempt routes
  const resultRoute = TEST_RESULT_ROUTES[testType];
  const attemptRoute = TEST_ATTEMPT_ROUTES[testType];
  
  /**
   * Builds a URL for a specific test
   * @param {string} testId - The test ID
   * @param {string} [section] - Optional section (listening, reading, writing, speaking)
   * @returns {string} The full URL path
   */
  const getTestUrl = (testId, section) => {
    if (section) {
      return `${baseRoute}/${testId}/${section}`;
    }
    return `${baseRoute}/${testId}`;
  };
  
  /**
   * Builds a URL for test results
   * @param {string} testId - The test ID
   * @param {string} attemptId - The attempt ID
   * @returns {string} The full results URL path
   */
  const getResultsUrl = (testId, attemptId) => {
    return `${baseRoute}/${testId}/results/${attemptId}`;
  };
  
  /**
   * Builds a URL for attempt result page
   * @param {string} testId - The test ID
   * @param {string} attemptId - The attempt ID
   * @returns {string} The full attempt result URL path
   */
  const getAttemptResultUrl = (testId, attemptId) => {
    return `${resultRoute}/${testId}/attempt-result/${attemptId}`;
  };
  
  /**
   * Builds a URL for attempts log page
   * @param {string} testId - The test ID
   * @returns {string} The full attempts log URL path
   */
  const getAttemptsUrl = (testId) => {
    return `${attemptRoute}/${testId}/attempts`;
  };
  
  return {
    testType,
    baseRoute,
    backRoute,
    resultRoute,
    attemptRoute,
    getTestUrl,
    getResultsUrl,
    getAttemptResultUrl,
    getAttemptsUrl,
  };
}

export default useTestNavigation;
